import { z } from 'zod';
import { raritySchema } from '../rarity';

/**
 * **A extração: como uma expedição acaba, e o que fica dela.**
 *
 * ## As duas maneiras de sair
 *
 * Um piloto sai do mundo aberto de exatamente duas formas, e as duas passam por
 * aqui: **extraído**, com tudo o que trazia a virar propriedade segura, ou
 * **morto**, com tudo o que trazia já dentro de uma caixa pública que outra
 * pessoa vai levar.
 *
 * As duas produzem um relatório, as duas libertam a localização, as duas tiram
 * a nave do mundo e as duas preparam o regresso à caça. O que muda é para onde
 * foram os bens — e essa decisão já foi tomada antes de se chegar aqui.
 *
 * ## O ponto sem volta
 *
 * `economic_committed` é a fronteira. **Antes** dela, a carga ainda é
 * propriedade de campo, não existe uma única linha segura, e uma falha não tira
 * nada a ninguém: o piloto recupera o controlo e continua a voar. **Depois**
 * dela não há regresso ao mundo aberto — a nave não recebe dano nem comandos, e
 * os itens não voltam à carga por caminho nenhum.
 *
 * O que sobra depois do compromisso é **entrega e navegação**: libertar a
 * localização, tirar o casco da sala, e dizer ao piloto para onde vai. Cada uma
 * delas pode falhar e cada uma delas é repetida até aterrar, porque nenhuma
 * delas pode desfazer o que a transação já escreveu.
 *
 * ## Não há transação distribuída
 *
 * Aqui como na travessia (ADR 0068) e na PT (ADR 0069): há uma escrita que é o
 * ponto de linearização, e tudo o resto converge para ela.
 */

/* -------------------------------------------------------------------------- */
/*  Versões                                                                   */
/* -------------------------------------------------------------------------- */

/** A versão da extração. A dela, e de mais nada. */
export const OPEN_WORLD_EXTRACTION_VERSION = 2;

/**
 * A versão do relatório da expedição. **A dele.**
 *
 * Separada da extração porque os dois têm tempos de vida diferentes: uma
 * extração acaba em segundos e um relatório fica para sempre. Um gate que
 * acrescente contadores de mobs, bosses ou recursos sobe esta e não aquela.
 */
export const OPEN_WORLD_EXPEDITION_REPORT_VERSION = 2;

/* -------------------------------------------------------------------------- */
/*  A máquina de estados                                                      */
/* -------------------------------------------------------------------------- */

/**
 * **Os seis estados, e a fronteira que os divide em dois.**
 *
 * | estado | os bens | a nave |
 * | --- | --- | --- |
 * | `pending` | de campo | congelada, e ainda no mundo |
 * | `failed_before_commit` | de campo | devolvida ao controlo do piloto |
 * | **`economic_committed`** | **seguros** | não recebe dano nem comandos |
 * | `location_released` | seguros | já não pertence a quadrante nenhum |
 * | `hunt_ready` | seguros | fora do mundo, com destino |
 * | `finalized` | seguros | nada por fazer |
 *
 * A única transição que não tem volta é a terceira. As três seguintes são
 * entrega, e uma entrega repete-se até aterrar.
 */
export const OPEN_WORLD_EXTRACTION_STATES = [
  'pending',
  'failed_before_commit',
  'economic_committed',
  'location_released',
  'hunt_ready',
  'finalized',
] as const;
export type OpenWorldExtractionState = (typeof OPEN_WORLD_EXTRACTION_STATES)[number];

/** Como a expedição acabou. Duas, e são exaustivas. */
export const OPEN_WORLD_EXTRACTION_OUTCOMES = ['extracted', 'died'] as const;
export type OpenWorldExtractionOutcome = (typeof OPEN_WORLD_EXTRACTION_OUTCOMES)[number];

/**
 * Porque uma extração falhou **antes** do compromisso. Fechada.
 *
 * Todas elas deixam tudo exatamente como estava: a carga é de campo, não há
 * linha segura nenhuma, e o piloto recupera o controlo da nave.
 */
export const OPEN_WORLD_EXTRACTION_REFUSALS = [
  /** Esta conta não tem carga neste mundo. Não há expedição para acabar. */
  'no_cargo',
  /**
   * A carga já foi consumida — por uma morte, ou por uma extração anterior.
   *
   * É esta a recusa que decide a corrida entre morrer e extrair: quem chegar
   * primeiro sela a carga, e quem chegar depois lê o selo.
   */
  'already_sealed',
  /**
   * O inventário seguro recusou um item: um nível acima do teto da definição,
   * uma definição desativada, um id que o catálogo não conhece.
   *
   * **Recusado antes do compromisso e sem perder nada.** Uma extração parcial
   * não existe: ou entram todos, ou não entra nenhum e a carga fica onde está.
   */
  'vault_refused',
  /** O ouro de campo não cabe na capacidade da moeda. Verificado antes de limpar. */
  'gold_overflow',
  /** A autoridade não respondeu. Nada mudou, e a ordem repete-se. */
  'unavailable',
] as const;
export type OpenWorldExtractionRefusal = (typeof OPEN_WORLD_EXTRACTION_REFUSALS)[number];

/* -------------------------------------------------------------------------- */
/*  A identidade                                                              */
/* -------------------------------------------------------------------------- */

/** O separador. Nenhum uuid o contém, portanto duas extrações não colidem. */
const EXTRACTION_ID_SEPARATOR = '|';

/**
 * **O id de uma extração, derivado e nunca cunhado.**
 *
 * Pela mesma razão que o id de uma morte é: a operação atravessa uma fronteira
 * que pode falhar, e a sala tem de poder repetir o pedido com **o mesmo id**
 * através de uma evicção. Um id aleatório obrigaria a escrevê-lo antes de
 * perguntar; um derivado é uma função do que já aconteceu.
 *
 * O `key` é o que distingue duas saídas da mesma conta na mesma expedição, e
 * são as duas coisas que já são únicas por vida de casco: o `commandId` da
 * canalização que a começou, ou o `deathId` que a morte derivou.
 */
export function openWorldExtractionId(input: {
  readonly worldId: string;
  readonly accountId: string;
  readonly expeditionId: string;
  readonly outcome: OpenWorldExtractionOutcome;
  readonly key: string;
}): string {
  return [input.worldId, input.accountId, input.expeditionId, input.outcome, input.key].join(
    EXTRACTION_ID_SEPARATOR,
  );
}

/**
 * **A expedição que uma extração acaba, lida do próprio id** (gate V28I.BY).
 *
 * O id é `mundo|conta|expedição|desfecho|chave`, portanto a expedição está sempre
 * lá dentro. É daqui que a sala a tira para pedir o compromisso e a libertação,
 * e não de um casco que pode já ter saído: um compromisso pedido com uma
 * expedição vazia nunca pode acontecer.
 *
 * A chave de uma morte tem separadores próprios, e por isso só as três primeiras
 * posições são lidas. `null` para um id que não tem a forma.
 */
export function openWorldExtractionExpeditionOf(extractionId: string): string | null {
  const parts = extractionId.split(EXTRACTION_ID_SEPARATOR);
  const expeditionId = parts[2];
  const outcome = parts[3];
  if (
    parts.length < 5 ||
    expeditionId === undefined ||
    expeditionId === '' ||
    (outcome !== 'extracted' && outcome !== 'died')
  ) {
    return null;
  }
  return expeditionId;
}

export const openWorldExtractionIdSchema = z.string().min(1).max(512);

/* -------------------------------------------------------------------------- */
/*  O que viaja                                                               */
/* -------------------------------------------------------------------------- */

const decimalSchema = z.string().regex(/^(0|[1-9][0-9]*)$/);

/**
 * Um item que passou a ser seguro, como o recibo o regista.
 *
 * `from` e `to` são o id de campo e o id da linha segura. **Iguais sempre que
 * possível** — e é possível, porque as duas tabelas guardam uuids e a linha de
 * campo desaparece na mesma transação em que a segura nasce. Ficam os dois
 * escritos na mesma forma para que um gate futuro que seja obrigado a mudar de
 * id não tenha de mudar o recibo.
 */
export const openWorldExtractedItemSchema = z
  .object({
    from: z.uuid(),
    to: z.uuid(),
    definitionId: z.string().min(1).max(128),
    rarity: raritySchema,
    level: z.number().int().min(1),
  })
  .strict();

export type OpenWorldExtractedItem = z.infer<typeof openWorldExtractedItemSchema>;

/** Uma captura que passou a ser segura. O id é preservado, sempre. */
export const openWorldExtractedCatchSchema = z
  .object({
    from: z.uuid(),
    to: z.uuid(),
    speciesId: z.string().min(1).max(128),
    rarity: raritySchema,
    weightMilligrams: z.number().int().min(1),
  })
  .strict();

export type OpenWorldExtractedCatch = z.infer<typeof openWorldExtractedCatchSchema>;

/**
 * **Onde a caixa de uma morte ficou, o que lá ficou, e até quando.**
 * Só num relatório `died`.
 *
 * Os dois totais chegaram na versão 2 (gate V28I.AV). Sem eles, a única coisa
 * que o piloto via de uma morte era `Ouro transferido: 0` — que é a frase de
 * uma extração vazia e não a de uma nave destruída. O que ele precisa de saber
 * é o que PERDEU, e isso não estava em lado nenhum da forma pública.
 *
 * Eles são lidos da liquidação, dentro da transação que a fecha, e não do
 * estado da caixa no momento em que alguém abre o recibo: uma caixa saqueada a
 * meio diria "zero itens", que é verdade sobre a caixa e mentira sobre a morte.
 */
export const openWorldReportBoxSchema = z
  .object({
    boxId: z.uuid(),
    rowIndex: z.number().int().min(0),
    columnIndex: z.number().int().min(0),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** Instante absoluto em milissegundos. Quinze minutos de relógio. */
    expiresAtMs: z.number().int().min(0),
    /**
     * **Quantos itens ficaram lá dentro** (versão 2).
     *
     * O total canónico da carga, peixes incluídos: uma captura é um item físico
     * do porão e ocupa um espaço como qualquer outro. Separá-los aqui seria
     * inventar uma distinção que a persistência não faz.
     */
    items: z.number().int().min(0),
    /** **O Ouro de campo que ficou lá dentro** (versão 2), em decimal canónico. */
    gold: decimalSchema,
  })
  .strict();

/**
 * **O relatório de uma expedição.** Imutável, versionado, e legível por id.
 *
 * Escrito na mesma transação que move os bens, o que faz dele a explicação da
 * escrita e não um resumo construído depois. Pode ser lido outra vez pelo id
 * sem repetir nada: o recibo é a linha, e a linha já existe.
 *
 * `ext` é o degrau que este gate deixa preparado. Contadores de mobs, de
 * bosses, de recursos e de tudo o que ainda não existe entram lá, com a versão
 * do relatório a subir — e não como colunas que ninguém sabe preencher hoje.
 */
export const openWorldExpeditionReportSchema = z
  .object({
    v: z.literal(OPEN_WORLD_EXPEDITION_REPORT_VERSION),
    reportId: z.uuid(),
    extractionId: openWorldExtractionIdSchema,
    accountId: z.uuid(),
    expeditionId: z.uuid(),
    worldId: z.uuid(),
    /** De onde se saiu. O quadrante em que a expedição acabou. */
    quadrant: z
      .object({ rowIndex: z.number().int().min(0), columnIndex: z.number().int().min(0) })
      .strict(),
    outcome: z.enum(OPEN_WORLD_EXTRACTION_OUTCOMES),
    /** Quando a expedição começou. Null enquanto nada o souber dizer. */
    startedAtMs: z.number().int().min(0).nullable(),
    endedAtMs: z.number().int().min(0),
    /** O ouro que passou a ser seguro, em decimal canónico. `"0"` numa morte. */
    gold: decimalSchema,
    items: z.array(openWorldExtractedItemSchema),
    catches: z.array(openWorldExtractedCatchSchema),
    /** A capacidade congelada, e quantos espaços estavam ocupados no fim. */
    slots: z.number().int().min(0),
    usedFinal: z.number().int().min(0),
    /** Onde ficou o que se perdeu. Só numa morte. */
    box: openWorldReportBoxSchema.nullable(),
    /**
     * O degrau versionado. Vazio hoje, e deliberadamente.
     *
     * Nada neste gate conta mobs, bosses ou recursos, porque nada neste jogo os
     * produz ainda. Inventar contadores a zero seria inventar conteúdo.
     */
    ext: z.record(z.string().min(1).max(64), z.string().max(256)),
  })
  .strict();

export type OpenWorldExpeditionReport = z.infer<typeof openWorldExpeditionReportSchema>;

/**
 * **O recibo, como o piloto o recebe.**
 *
 * Um resumo do relatório e o destino autoritativo. Reenviado numa reconexão,
 * porque quem perdeu a ligação a meio da navegação tem de poder acabá-la — e
 * porque repetir a leitura de um recibo não repete transação nenhuma.
 *
 * **O destino é `hunt`, e é a única coisa que ele pode ser.** Não há seleção,
 * não há mapa e não há escolha: a expedição acabou, e o que existe do outro
 * lado é a caça automática que já existia antes de tudo isto.
 */
export const openWorldExtractionReceiptSchema = z
  .object({
    t: z.literal('xt'),
    v: z.literal(OPEN_WORLD_EXTRACTION_VERSION),
    id: openWorldExtractionIdSchema,
    state: z.enum(OPEN_WORLD_EXTRACTION_STATES),
    outcome: z.enum(OPEN_WORLD_EXTRACTION_OUTCOMES),
    /** Porque não, quando o estado é `failed_before_commit`. */
    r: z.enum(OPEN_WORLD_EXTRACTION_REFUSALS).nullable(),
    /** O relatório, quando já existe. Null antes do compromisso. */
    reportId: z.uuid().nullable(),
    gold: decimalSchema,
    items: z.number().int().min(0),
    catches: z.number().int().min(0),
    /** Onde ficou o que se perdeu, numa morte. */
    box: openWorldReportBoxSchema.nullable(),
    /**
     * Para onde o piloto vai a seguir. `null` até a localização ser libertada.
     *
     * Uma palavra e não uma rota: o servidor diz que esta conta pertence agora
     * à caça, e é a fronteira que já existe — a mesma que cria ou retoma a
     * sessão económica — que a leva lá.
     */
    to: z.literal('hunt').nullable(),
  })
  .strict();

export type OpenWorldExtractionReceipt = z.infer<typeof openWorldExtractionReceiptSchema>;
